import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Loader2, Sparkles, Crown, Zap, ArrowUpRight, Clock, ShieldCheck, AlertCircle, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const PLAN_ICON = { free: Sparkles, pro: Zap, business: Crown };
const STATUS_LABEL = { trial: 'Masa Trial', active: 'Aktif', expired: 'Trial Berakhir', suspended: 'Ditangguhkan' };
const STATUS_STYLE = {
  trial: 'border-sky-200 bg-sky-50 text-sky-700',
  active: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  expired: 'border-orange-200 bg-orange-50 text-orange-700',
  suspended: 'border-red-200 bg-red-50 text-red-700',
};

function daysLeft(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

function UsageBar({ label, used, limit }) {
  const unlimited = limit === -1;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
  const near = !unlimited && pct >= 80;
  return (
    <div data-testid="billing-usage-progress">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className={`font-medium tabular-nums ${near ? 'text-orange-600' : ''}`}>
          {used}{unlimited ? '' : ` / ${limit}`}{unlimited && <span className="ml-1 text-muted-foreground">(tanpa batas)</span>}
        </span>
      </div>
      {!unlimited && <Progress value={pct} className="mt-1.5 h-2" />}
    </div>
  );
}

function BillingSkeleton() {
  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="billing-loading-skeleton">
      <div className="space-y-2"><Skeleton className="h-6 w-40" /><Skeleton className="h-4 w-64" /></div>
      <div className="rounded-2xl border bg-card p-5">
        <div className="flex items-center gap-3"><Skeleton className="h-11 w-11 rounded-xl" /><div className="space-y-2"><Skeleton className="h-5 w-32" /><Skeleton className="h-3 w-20" /></div></div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-72 w-full rounded-2xl" />)}</div>
    </div>
  );
}

export default function Billing() {
  const { isOwner } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setError(false);
    try {
      const r = await api.get('/billing/me');
      setData(r.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle return from Xendit hosted checkout (?pay=success|failed)
  useEffect(() => {
    const pay = params.get('pay');
    if (!pay) return;
    (async () => {
      if (pay === 'success') {
        try {
          const r = await api.post('/billing/xendit/sync');
          if (r.data?.updated || r.data?.status === 'PAID') {
            toast.success('Pembayaran berhasil! Paket Anda sudah aktif.');
          } else {
            toast.info('Pembayaran sedang diproses. Paket aktif otomatis setelah terkonfirmasi.');
          }
        } catch {
          toast.error('Gagal memverifikasi status pembayaran.');
        }
      } else if (pay === 'failed') {
        toast.error('Pembayaran dibatalkan atau gagal. Silakan coba lagi.');
      }
      params.delete('pay');
      setParams(params, { replace: true });
      load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpgrade = async (plan) => {
    setBusy(plan);
    try {
      const r = await api.post('/billing/xendit/create-invoice', { plan, return_origin: window.location.origin });
      if (r.data?.invoice_url) {
        toast.success('Mengalihkan ke halaman pembayaran aman...');
        window.location.href = r.data.invoice_url;
        return;
      }
      toast.error('Tidak mendapatkan tautan pembayaran.');
      setBusy('');
    } catch (e) {
      const code = e?.response?.status;
      if (code === 503) {
        // Gateway not configured — fall back to manual activation request
        try {
          const r2 = await api.post('/billing/request-upgrade', { plan });
          toast.success(r2.data.message);
          load();
        } catch {
          toast.error('Gagal memproses upgrade.');
        }
      } else {
        !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal membuat pembayaran.');
      }
      setBusy('');
    }
  };

  if (loading) return <BillingSkeleton />;

  if (error || !data) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col items-center justify-center gap-3 py-20 text-center" data-testid="billing-error-state">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive"><AlertCircle className="h-6 w-6" /></div>
        <p className="text-sm text-muted-foreground">Gagal memuat data langganan.</p>
        <Button variant="outline" onClick={() => { setLoading(true); load(); }} data-testid="billing-retry-button">
          <RefreshCw className="mr-2 h-4 w-4" /> Coba lagi
        </Button>
      </div>
    );
  }

  const Icon = PLAN_ICON[data.plan] || Sparkles;
  const dleft = daysLeft(data.trial_ends_at);
  const limits = data.limits || {};
  const usage = data.usage || {};

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <h1 className="font-display text-xl font-bold">Langganan</h1>
        <p className="text-sm text-muted-foreground">Paket, penggunaan, dan upgrade workshop Anda.</p>
      </div>

      {/* Current plan */}
      <div data-testid="billing-current-plan-card" className="rounded-2xl border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display text-lg font-semibold">Paket {data.plan_info?.name}</span>
                <Badge variant="outline" className={`rounded-full ${STATUS_STYLE[data.status] || ''}`}>{STATUS_LABEL[data.status] || data.status}</Badge>
              </div>
              <div className="text-xs text-muted-foreground">{data.plan_info?.price_label}{data.plan_info?.price_suffix || ''}</div>
            </div>
          </div>
          {data.status === 'trial' && dleft != null && (
            <div className="flex items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-700">
              <Clock className="h-3.5 w-3.5" /> Sisa {dleft > 0 ? dleft : 0} hari trial
            </div>
          )}
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <UsageBar label="Pengguna (tim)" used={usage.users || 0} limit={limits.users} />
          <UsageBar label="Pelanggan" used={usage.customers || 0} limit={limits.customers} />
          <UsageBar label="Penawaran bulan ini" used={usage.quotes_this_month || 0} limit={limits.quotes_per_month} />
          <UsageBar label="AI bulan ini" used={usage.ai_this_month || 0} limit={limits.ai_per_month} />
        </div>

        {data.requested_plan && data.status !== 'active' && (
          <div className="mt-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <Clock className="h-4 w-4 shrink-0" /> Menunggu pembayaran paket <b className="capitalize">{data.requested_plan}</b>. Paket aktif otomatis setelah pembayaran terkonfirmasi.
          </div>
        )}
      </div>

      {/* Plans */}
      {isOwner ? (
        <div className="grid gap-4 lg:grid-cols-3">
          {(data.all_plans || []).map((p) => {
            const isCurrent = p.key === data.plan;
            const PIcon = PLAN_ICON[p.key] || Sparkles;
            return (
              <div key={p.key} data-testid={`billing-plan-${p.key}`}
                className={`flex flex-col rounded-2xl border bg-card p-5 transition-[box-shadow,border-color] duration-200 hover:shadow-md ${p.featured ? 'border-primary ring-1 ring-primary/20' : 'border-border'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><PIcon className="h-4 w-4 text-primary" /><span className="font-display font-semibold">{p.name}</span></div>
                  {p.featured && <Badge className="bg-primary text-primary-foreground">Populer</Badge>}
                </div>
                <div className="mt-3 flex items-end gap-1">
                  <span className="font-display text-2xl font-bold tabular-nums">{p.price ? `Rp ${Number(p.price).toLocaleString('id-ID')}` : 'Gratis'}</span>
                  <span className="pb-1 text-xs text-muted-foreground">{p.price ? '/bln' : p.price_label}</span>
                </div>
                <ul className="mt-4 flex-1 space-y-2">
                  {p.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2 text-xs text-muted-foreground"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /> {h}</li>
                  ))}
                </ul>
                {isCurrent ? (
                  <Button disabled variant="outline" className="mt-5 w-full rounded-xl" data-testid={`billing-current-${p.key}-button`}>Paket Saat Ini</Button>
                ) : p.key === 'free' ? (
                  <Button disabled variant="ghost" className="mt-5 w-full rounded-xl text-muted-foreground">—</Button>
                ) : (
                  <Button onClick={() => handleUpgrade(p.key)} disabled={!!busy} data-testid={p.key === 'pro' ? 'billing-upgrade-button' : `billing-upgrade-${p.key}-button`}
                    variant={p.featured ? 'default' : 'outline'} className="mt-5 w-full rounded-xl">
                    {busy === p.key ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowUpRight className="mr-1.5 h-4 w-4" />} Upgrade ke {p.name}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">Hanya owner yang dapat mengelola langganan.</div>
      )}

      <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5" /> Pembayaran aman via Xendit — QRIS, Virtual Account & e-wallet.
      </p>
    </div>
  );
}
